import React, { useState, useRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import { makeStyles, useTheme } from '@mui/styles'

import IconPencil from '@assets/images/icons/pencil.svg'

import TextInput from './text-input'
import SVGIcon from '../../svg-icon/svg-icon'
import clsx from 'clsx'
import { splitFilename } from '@/modules/_helpers/splitFilename'

const TextInputLabel = React.forwardRef(function TextInputLabel(props, forwardRef) {
  const {
    value,
    readOnly,
    fileNameMode,
    onBlur,
    className,
    onKeyDown,
    ...rest
  } = props

  const internalRef = useRef(null)

  useImperativeHandle(forwardRef, () => internalRef.current, [])

  const classes = useStyles(props)
  const theme = useTheme()
  const [isEditMode, setIsEditMode] = useState(props.isEditMode)

  const handleOnBlur = (value, name) => {
    setIsEditMode(false)
    onBlur && onBlur(value, name)
  }

  const handleStartEdit = () => {
    if (readOnly) return

    setIsEditMode(true)
    internalRef.current && internalRef.current.focus()
  }

  const handleOnKeyDown = (event) => {
    if (event.keyCode === 13) { // on hit Enter
      internalRef.current.blur()
    }
    onkeydown && onkeydown(event)
  }

  const { classes: { inputReadMode, ...restStyles } } = props

  if (isEditMode) {
    internalRef.current && internalRef.current.focus()
  }

  const editValue = fileNameMode
    ? splitFilename(value).name
    : value

  return (
    <div className={clsx(classes.container, !isEditMode && classes.readMode, className)}>
      <TextInput {...rest} value={editValue} classes={restStyles} onKeyDown={handleOnKeyDown} onBlur={handleOnBlur} autoFocus={props.isEditMode} ref={internalRef} />
      {
        !isEditMode && (
          <div className={inputReadMode}>
            <span>{value}</span>
            {!readOnly && <SVGIcon width='0.8em' height='0.8em' svg={IconPencil} className={classes.icon} fill={theme.black} onClick={handleStartEdit} />}
          </div>
        )
      }
    </div>
  )
})

TextInputLabel.defaultProps = {
  type: 'text',
  autoComplete: '',
  className: null,
  classes: {},
}

TextInputLabel.propTypes = {
  type: PropTypes.string,
  autoComplete: PropTypes.string,
  className: PropTypes.string,
  classes: PropTypes.object,
  useFormik: PropTypes.bool,
  readOnly: PropTypes.bool,
  fileNameMode: PropTypes.bool,
}

const useStyles = makeStyles(theme => ({
  container: (props) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    color: props.readOnly ? theme.disabledColor : theme.text,
  }),
  icon: {
    display: 'inline',
    paddingLeft: '5px',
    cursor: 'pointer',
    '&:hover svg': {
      fill: theme.primaryLight,
    },
  },
  readMode: {
    '& input': {
      height: '0px',
      borderWidth: '0px',
      padding: '0',
    },
  },
  inputReadMode: { }, // this fixes a console warning from material
}))

export default TextInputLabel
